import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { getTheme } from "@/lib/themes";
import Reveal from "@/components/Reveal";

const ThemeCollection = () => {
  const { themeSlug } = useParams();
  const theme = getTheme(themeSlug);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!theme) return;
    api.get("/products", { params: { theme: themeSlug, limit: 60 } })
      .then((r) => setProducts(r.data))
      .catch(() => {});
  }, [themeSlug, theme]);

  if (!theme) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center py-32">
        <p className="eyebrow mb-3">404</p>
        <h1 className="font-hero text-4xl mb-4">Theme not found</h1>
        <Link to="/shop" className="btn-link">Back to shop →</Link>
      </div>
    );
  }

  const p = theme.palette;

  return (
    <div style={{ background: p.bg, color: p.ink }} data-testid="theme-collection-page">
      {/* HERO */}
      <section className="relative min-h-[70vh] overflow-hidden" data-testid="theme-collection-hero">
        <motion.div className="absolute inset-0" initial={{ scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 1.4 }}>
          <img src={theme.backdrop_image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{
            background: `linear-gradient(180deg, ${p.ink}55 0%, ${p.bg}ee 80%),
                         radial-gradient(ellipse at 70% 40%, rgba(247,208,112,0.3), transparent 55%)`,
          }} />
        </motion.div>
        <div className="relative rieave-container h-full pt-28 md:pt-40 pb-16 text-center">
          <motion.p className="text-[11px] tracking-[0.36em] uppercase mb-5" style={{ color: p.gold }} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            ✦ The {theme.name} Edit ✦
          </motion.p>
          <motion.h1
            className="font-hero text-5xl md:text-8xl leading-[0.92] mb-6"
            style={{ color: p.ink }}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}
          >
            {theme.tagline.split(" ").slice(0, -1).join(" ")}{" "}
            <span className="italic" style={{ color: p.gold }}>{theme.tagline.split(" ").slice(-1)}</span>
          </motion.h1>
          <motion.p className="max-w-2xl mx-auto text-lg md:text-xl" style={{ color: `${p.ink}bb` }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
            {theme.intro}
          </motion.p>
        </div>
      </section>

      {/* CHARACTER INTRO STRIP */}
      <section className="py-20 md:py-28" style={{ background: p.surface }}>
        <div className="rieave-container grid md:grid-cols-12 gap-10 items-center">
          <Reveal className="md:col-span-5">
            <div className="relative">
              <img
                src={theme.hero_image}
                alt={theme.name}
                className="w-full aspect-[4/5] object-cover rounded-[2px]"
                style={{ boxShadow: `0 30px 60px -20px ${p.ink}40` }}
              />
              <div
                className="absolute inset-0 pointer-events-none mix-blend-soft-light"
                style={{ background: `radial-gradient(ellipse at 50% 80%, ${p.glow}70, transparent 65%)` }}
              />
            </div>
          </Reveal>
          <div className="md:col-span-7">
            <Reveal>
              <p className="text-[11px] tracking-[0.32em] uppercase mb-4" style={{ color: p.gold }}>
                {theme.story.eyebrow}
              </p>
              <h2 className="font-hero text-4xl md:text-5xl leading-[1.1] mb-6" style={{ color: p.ink }}>
                {theme.story.title}
              </h2>
              <p className="text-[17px] leading-[1.75]" style={{ color: `${p.ink}bb` }}>
                {theme.story.body}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PRODUCTS GRID */}
      <section className="py-20 md:py-28" style={{ background: p.bg }} data-testid="theme-products">
        <div className="rieave-container">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-3">
            <h2 className="font-hero text-3xl md:text-5xl" style={{ color: p.ink }}>
              Shop the <span className="italic" style={{ color: p.gold }}>{theme.name}</span> edit
            </h2>
            <p className="text-sm" style={{ color: `${p.ink}99` }}>{products.length} piece{products.length === 1 ? "" : "s"}</p>
          </div>

          {products.length === 0 ? (
            <div className="text-center py-20" data-testid="theme-empty">
              <Sparkles className="mx-auto mb-5" style={{ color: p.gold }} />
              <p className="font-hero text-2xl mb-3">New pieces are spinning on the wheel.</p>
              <p className="mb-8" style={{ color: `${p.ink}99` }}>The {theme.name} edit expands soon. In the meantime, explore the rest of the rieave world.</p>
              <Link to="/shop" className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-xs tracking-[0.24em] uppercase text-white" style={{ background: p.ink }}>
                Browse the full shop <ArrowRight size={12} />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7">
              {products.map((pr, i) => (
                <Reveal key={pr.id} delay={i * 0.06}>
                  <Link
                    to={`/product/${pr.slug}`}
                    className="group block"
                    data-testid={`theme-product-${pr.slug}`}
                  >
                    <div className="relative aspect-[4/5] overflow-hidden rounded-[2px] mb-5" style={{ boxShadow: `0 25px 50px -25px ${p.ink}50` }}>
                      <motion.img
                        src={pr.images?.[0]}
                        alt={pr.name}
                        className="w-full h-full object-cover"
                        whileHover={{ scale: 1.06 }}
                        transition={{ duration: 0.8 }}
                      />
                      <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
                           style={{ background: `linear-gradient(180deg, transparent 60%, ${p.ink}cc)` }} />
                      <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ background: p.glow, color: p.ink }}>
                        ✦ {theme.name}
                      </div>
                      {pr.compare_at_price > pr.price && (
                        <div className="absolute top-4 right-4 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.22em]" style={{ background: p.ink, color: "#fff" }}>
                          Limited
                        </div>
                      )}
                    </div>
                    <h3 className="font-hero text-2xl leading-tight mb-2" style={{ color: p.ink }}>{pr.name}</h3>
                    <p className="text-sm mb-3" style={{ color: `${p.ink}99` }}>{pr.short_description}</p>
                    <div className="flex items-baseline gap-2">
                      <span className="font-hero text-xl" style={{ color: p.ink }}>{formatINR(pr.price)}</span>
                      {pr.compare_at_price > pr.price && (
                        <span className="text-sm line-through" style={{ color: `${p.ink}55` }}>{formatINR(pr.compare_at_price)}</span>
                      )}
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ThemeCollection;
